/**
 * A page-at-a-time list of what automation rules did on a board: which rule,
 * what it wrote, whether that changed the row, who set it off and when. Shared
 * by a rule's history in the automations dialog and a row's activity in its
 * side panel, which differ only in the filter and in whether each entry names
 * its rule.
 */
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Skeleton } from '@monksflow/monks-ui';

import {
  trackerAutomationRunsQueryOptions,
  type TrackerAutomationRun,
  type TrackerAutomationRunOutcome,
} from '@/entities/tracker-automation';
import { useUserDisplay } from '@/entities/user';
import { formatTimeAgo } from '@/shared/utils/dateUtils';

import {
  dateTimeText,
  describeRunChanges,
  describeRunEffects,
  listText,
} from '../../lib/automation-runs';
import type { CustomColumn, StatusOption } from '../../lib/useTrackersBoard';

const OUTCOME_VARIANT: Record<
  TrackerAutomationRunOutcome,
  'positive' | 'secondary' | 'destructive' | 'warning'
> = {
  applied: 'positive',
  no_change: 'secondary',
  failed: 'destructive',
  dropped_depth: 'warning',
  skipped_no_access: 'secondary',
  skipped_no_access_target: 'warning',
};

interface AutomationRunListProps {
  trackerId: string;
  filter: { automationId?: string; rowRef?: string };
  columns: CustomColumn[];
  groups: StatusOption[];
  /** How to name each entry's rule; absent when every entry is the same rule. */
  ruleLabel?: (run: TrackerAutomationRun) => string;
}

export function AutomationRunList({
  trackerId,
  filter,
  columns,
  groups,
  ruleLabel,
}: AutomationRunListProps) {
  const { t } = useTranslation('trackers');
  const { data, isLoading, isError, refetch, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery(trackerAutomationRunsQueryOptions(trackerId, filter));
  const runs = data?.pages.flatMap((page) => page.data) ?? [];

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2" aria-hidden>
        {['s1', 's2'].map((key) => (
          <Skeleton key={key} className="h-12 w-full rounded-lg" />
        ))}
      </div>
    );
  }
  if (isError) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground" role="alert">
        {t('trackersBoard.automationRuns.failedToLoad')}
        <Button type="button" variant="outline" size="sm" onClick={() => void refetch()}>
          {t('trackersBoard.automationsPanel.tryAgain')}
        </Button>
      </div>
    );
  }
  if (runs.length === 0 && !hasNextPage) {
    return (
      <p className="text-sm text-muted-foreground">{t('trackersBoard.automationRuns.empty')}</p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <ul className="flex flex-col gap-2">
        {runs.map((run) => (
          <RunEntry
            key={run.id}
            run={run}
            columns={columns}
            groups={groups}
            rule={ruleLabel?.(run)}
          />
        ))}
      </ul>
      {hasNextPage ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-start"
          disabled={isFetchingNextPage}
          onClick={() => void fetchNextPage()}
        >
          {t('trackersBoard.automationRuns.loadMore')}
        </Button>
      ) : null}
      <p className="text-xs text-muted-foreground">{t('trackersBoard.automationRuns.retention')}</p>
    </div>
  );
}

function RunEntry({
  run,
  columns,
  groups,
  rule,
}: {
  run: TrackerAutomationRun;
  columns: CustomColumn[];
  groups: StatusOption[];
  rule?: string;
}) {
  const { t } = useTranslation('trackers');
  const actor = useUserDisplay(run.actorUserId);
  const changes = [
    ...describeRunChanges(run.changes, columns, groups).map(
      (change) => `${change.label}: ${change.value}`,
    ),
    ...describeRunEffects(run.effects),
  ];
  const meta = [
    formatTimeAgo(run.firedAt),
    run.actorUserId && actor.name
      ? t('trackersBoard.automationRuns.setOffBy', { name: actor.name })
      : null,
  ]
    .filter(Boolean)
    .join(t('trackersBoard.automationsPanel.metaSeparator'));

  return (
    <li className="flex flex-col gap-1 rounded-lg border border-border p-3">
      <div className="flex items-start justify-between gap-3">
        <span className="min-w-0 text-sm text-foreground">
          {rule ? <span className="font-medium">{rule}</span> : null}
          {rule && changes.length > 0 ? t('trackersBoard.automationsPanel.metaSeparator') : null}
          {listText(changes)}
        </span>
        <Badge variant={OUTCOME_VARIANT[run.outcome]} size="sm" className="shrink-0">
          {t(`trackersBoard.automationRuns.outcomes.${run.outcome}`)}
        </Badge>
      </div>
      <span className="text-xs text-muted-foreground" title={dateTimeText(run.firedAt)}>
        {meta}
      </span>
    </li>
  );
}
